export type ApiResponse<T> = {
	success: boolean;
	data: T;
	message: string;
	errorCode?: string;
};

export class NetworkError extends Error {
	constructor(message = "fetch failed") {
		super(message);
		this.name = "NetworkError";
	}
}

export type ApiErrorInit = {
	errorCode: string;
	/** The HTTP status the platform answered with; absent when the error was raised client-side. */
	status?: number;
	data?: Record<string, unknown>;
};

const stringField = (data: Record<string, unknown> | undefined, key: string): string | undefined => {
	const value = data?.[key];
	return typeof value === "string" && value.length > 0 ? value : undefined;
};

export class ApiError extends Error {
	public errorCode: string;
	/** 0 when the error was raised client-side (a config the app cannot use). */
	public status: number;
	public data?: Record<string, unknown>;
	/** The provider's own code and message, when the platform carried them in `data`. */
	public providerCode?: string;
	public providerMessage?: string;
	constructor(message: string, init: ApiErrorInit) {
		super(message);
		this.errorCode = init.errorCode;
		this.status = init.status ?? 0;
		this.data = init.data;
		this.providerCode = stringField(init.data, "providerCode");
		this.providerMessage = stringField(init.data, "providerMessage");
		this.name = "ApiError";
	}
}

export class AbortError extends Error {
	constructor(message = "Request aborted") {
		super(message);
		this.name = "AbortError";
	}
}

export const isAbortError = (error: unknown): boolean =>
	error instanceof AbortError || (error instanceof Error && error.name === "AbortError");

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;
